import React, { useEffect, useMemo, useState, lazy, Suspense } from 'react';
import { ThemeProvider, CssBaseline, CircularProgress, Box } from '@mui/material';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';

import { lightTheme, darkTheme } from './theme';
import { AuthProvider } from './context/AuthContext';
import { NotificationProvider } from './context/NotificationContext';
import { SelectionProvider } from './context/SelectionContext';
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';
import NonSupervisorRoute from './components/NonSupervisorRoute';
import EmployeeBlockedRoute from './components/EmployeeBlockedRoute';

import MainLayout from './components/layout/MainLayout';
import LoginPage from './pages/LoginPage';
import ContactsPage from './pages/ContactsPage';
import BranchesPage from './pages/BranchesPage';
import LandingPage from './pages/LandingPage';
import DashboardPage from './pages/DashboardPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ChangePasswordPage from './pages/ChangePasswordPage';
import ShareViewerPage from './pages/ShareViewerPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import DeleteAccountPage from './pages/DeleteAccountPage';

import { auditsEnabled, reportsEnabled } from './config/featureFlags';

const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const DocumentsPage = lazy(() => import('./pages/DocumentsPage'));
const AuditPage = lazy(() => import('./pages/AuditPage'));
const ReportsPage = lazy(() => import('./pages/ReportsPage'));

const LazyFallback = (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
    <CircularProgress />
  </Box>
);

const THEME_STORAGE_KEY = 'mfs-theme-mode';

const resolveInitialTheme = () => {
  if (typeof window === 'undefined') return 'dark';
  const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
  if (stored === 'light' || stored === 'dark') return stored;
  return 'dark';
};

function App() {
  const [themeMode, setThemeMode] = useState(resolveInitialTheme);
  const toggleTheme = () => {
    setThemeMode(prev => {
      const next = prev === 'dark' ? 'light' : 'dark';
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(THEME_STORAGE_KEY, next);
      }
      return next;
    });
  };
  const theme = useMemo(() => (themeMode === 'dark' ? darkTheme : lightTheme), [themeMode]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(THEME_STORAGE_KEY, themeMode);
    }
  }, [themeMode]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <NotificationProvider>
        <AuthProvider>
          <SelectionProvider>
            <Router>
              <Suspense fallback={LazyFallback}>
              <Routes>
              <Route path="/" element={<LandingPage themeMode={themeMode} onToggleTheme={toggleTheme} />} />
              {/* Public Route */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/share/:shareId" element={<ShareViewerPage />} />
              <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
              <Route path="/delete-account" element={<DeleteAccountPage />} />
              <Route
                path="/change-password"
                element={
                  <ProtectedRoute>
                    <ChangePasswordPage />
                  </ProtectedRoute>
                }
              />

              {/* Main authenticated section uses the MainLayout */}
              <Route
                element={
                  <ProtectedRoute>
                    <MainLayout themeMode={themeMode} onToggleTheme={toggleTheme} />
                  </ProtectedRoute>
                }
              >
                <Route path="dashboard" element={<DashboardPage />} />
                <Route
                  path="businesses"
                  element={
                    <AdminRoute>
                      <AdminDashboard />
                    </AdminRoute>
                  }
                />
                <Route path="contacts" element={<ContactsPage />} />
                <Route
                  path="branches"
                  element={
                    <NonSupervisorRoute>
                      <BranchesPage />
                    </NonSupervisorRoute>
                  }
                />
                <Route path="documents" element={
                  <EmployeeBlockedRoute>
                    <DocumentsPage />
                  </EmployeeBlockedRoute>
                } />
                {auditsEnabled && (
                  <Route path="audits" element={
                    <EmployeeBlockedRoute>
                      <AuditPage />
                    </EmployeeBlockedRoute>
                  } />
                )}
                {reportsEnabled && (
                  <Route path="reports" element={<ReportsPage />} />
                )}
              </Route>

              {/* Legacy dashboard route redirects */}
              <Route path="/dashboard/businesses" element={<Navigate to="/businesses" replace />} />
              <Route path="/dashboard/contacts" element={<Navigate to="/contacts" replace />} />
              <Route path="/dashboard/branches" element={<Navigate to="/branches" replace />} />
              <Route path="/dashboard/documents" element={<Navigate to="/documents" replace />} />

              {/* --- CHANGE #2: The catch-all route now points to the smart redirector --- */}
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
              </Suspense>
          </Router>
          </SelectionProvider>
        </AuthProvider>
      </NotificationProvider>
    </ThemeProvider>
  );
}

export default App;
